import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { inr, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  Receipt, CurrencyInr, Printer, WhatsappLogo, Envelope, DownloadSimple,
  MagnifyingGlass, DotsThreeVertical, BookOpen, FileText, CheckCircle, Warning,
  CreditCard, ShoppingCart,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import CustomerLink from "@/components/CustomerLink";

const STATUS_TONE = {
  Paid: "bg-emerald-600 text-white hover:bg-emerald-600",
  Unpaid: "bg-amber-50 text-amber-700 border-amber-300",
};
const PAY_METHODS = ["Cash", "UPI", "PhonePe", "Google Pay", "Paytm", "Card", "Net Banking", "NEFT", "RTGS", "IMPS", "Wallet", "Cheque"];
const METHOD_COLORS = { Cash: "#EA580C", UPI: "#16A34A", Card: "#1E3A8A", "Net Banking": "#7C3AED", Wallet: "#F59E0B" };

function inWindow(iso, days) {
  if (!iso || !days) return true;
  const d = new Date(iso);
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  return d >= cutoff;
}

function Kpi({ label, value, icon: Icon, tone, testid, onClick, active }) {
  const t = {
    orange: "bg-orange-50 text-orange-600 border-orange-200",
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
  }[tone];
  return (
    <Card
      data-testid={testid}
      onClick={onClick}
      className={`p-5 bg-white border-slate-200 cursor-pointer hover:-translate-y-px hover:shadow-md transition-[transform,box-shadow] duration-200 ${active ? "ring-2 ring-orange-500 ring-offset-1" : ""}`}
    >
      <div className={`w-10 h-10 rounded-md flex items-center justify-center border ${t} mb-3`}><Icon size={20} weight="duotone" /></div>
      <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>{value}</div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mt-1">{label}</div>
    </Card>
  );
}

export default function Billing() {
  const [rows, setRows] = useState([]);
  const [posRows, setPosRows] = useState([]);
  const [source, setSource] = useState("Application"); // Application | POS
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [methodFilter, setMethodFilter] = useState("All");
  const [period, setPeriod] = useState("all"); // all | today | 7 | 30
  const [receipt, setReceipt] = useState(null);
  const [pay, setPay] = useState(null);
  const [ledger, setLedger] = useState(null); // { customer_id, name, bills: [...] }
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    const [a, p] = await Promise.all([
      api.get("/applications"),
      api.get("/pos/bills", { params: { days: 365 } }).catch(() => ({ data: [] })),
    ]);
    setRows(a.data);
    setPosRows(p.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Deep link: /billing?status=Unpaid, ?status=Paid, ?period=today, ?source=POS
  useEffect(() => {
    const s = params.get("status");
    if (s === "Paid" || s === "Unpaid" || s === "All") setStatusFilter(s);
    const p = params.get("period");
    if (["today", "7", "30", "all"].includes(p)) setPeriod(p);
    const src = params.get("source");
    if (src === "POS" || src === "Application") setSource(src);
    if (s || p || src) setParams({});
  }, [params, setParams]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    const days = period === "today" ? 1 : period === "7" ? 7 : period === "30" ? 30 : 0;
    const baseRows = source === "POS"
      ? posRows.map((b) => ({
          // normalise POS bill shape to look like an application row for the existing table
          id: b.id,
          app_no: b.pos_no,
          created_at: b.created_at,
          paid_at: b.paid_at,
          customer_id: b.customer_id || "",
          customer_name: b.customer_name || "Walk-in",
          service_name: (b.items || []).map((i) => i.name).slice(0, 2).join(", ") + ((b.items || []).length > 2 ? ` +${b.items.length - 2}` : ""),
          department: b.bill_type === "NonGST" ? "Cash Memo · Non-GST" : "GST Invoice · Retail",
          gst_amount: b.gst_amount || 0,
          total: b.total || 0,
          payment_status: b.payment_status === "Partial" ? "Unpaid" : b.payment_status,
          payment_method: (b.payments || []).map((p) => p.method).join(" + "),
          payment_txn_ref: (b.payments || []).map((p) => p.txn_ref).filter(Boolean).join(", "),
          _isPos: true,
          _raw: b,
        }))
      : rows;
    return baseRows.filter((r) => {
      if (statusFilter === "Paid" && r.payment_status !== "Paid") return false;
      if (statusFilter === "Unpaid" && r.payment_status === "Paid") return false;
      if (methodFilter !== "All" && (r.payment_method || "").split(" + ")[0] !== methodFilter) return false;
      if (days) {
        const anchor = r.paid_at || r.created_at;
        if (!inWindow(anchor, days)) return false;
      }
      if (query) {
        const bag = [r.app_no, r.customer_name, r.service_name, r.department, r.portal_app_no, r.payment_txn_ref].filter(Boolean).join(" ").toLowerCase();
        if (!bag.includes(query)) return false;
      }
      return true;
    });
  }, [rows, posRows, source, q, statusFilter, methodFilter, period]);

  const kpis = useMemo(() => {
    const paid = filtered.filter((r) => r.payment_status === "Paid");
    const unpaid = filtered.filter((r) => r.payment_status !== "Paid");
    const cash = paid.filter((r) => r.payment_method === "Cash").reduce((s, r) => s + (r.total || 0), 0);
    const online = paid.filter((r) => ["UPI", "Card", "Net Banking", "Wallet"].includes(r.payment_method)).reduce((s, r) => s + (r.total || 0), 0);
    // POS split — count of POS bills paid today / all-time regardless of current source filter
    const cutoffDays = period === "today" ? 1 : period === "7" ? 7 : period === "30" ? 30 : 0;
    const posInWindow = posRows.filter((b) => !cutoffDays || inWindow(b.paid_at || b.created_at, cutoffDays));
    const posBills = posInWindow.length;
    const posCollection = posInWindow.filter((b) => b.payment_status === "Paid").reduce((s, b) => s + (b.total || 0), 0);
    // GST / non-GST split — applications are always GST (18%); POS carries bill_type explicitly
    const gstCount = source === "POS"
      ? posInWindow.filter((b) => (b.bill_type || "GST") === "GST").length
      : filtered.length; // all application bills carry GST
    const nonGstCount = source === "POS"
      ? posInWindow.filter((b) => (b.bill_type || "GST") === "NonGST").length
      : 0;
    return {
      count: filtered.length,
      collected: paid.reduce((s, r) => s + (r.total || 0), 0),
      cash, online,
      gst: paid.reduce((s, r) => s + (r.gst_amount || 0), 0),
      outstanding: unpaid.reduce((s, r) => s + (r.total || 0), 0),
      gstCount, nonGstCount,
      posBills, posCollection,
    };
  }, [filtered, posRows, period, source]);

  const quickCash = async (r) => {
    try {
      const { data } = await api.post(`/applications/${r.id}/pay`, { method: "Cash", txn_ref: "" });
      toast.success(`Cash paid · ${inr(r.total)}`);
      load(); setReceipt(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const doPay = async () => {
    if (!pay) return;
    try {
      const { data } = await api.post(`/applications/${pay.id}/pay`, { method: pay.method, txn_ref: pay.txn_ref || "" });
      toast.success(`Payment recorded · ${pay.method}`);
      setPay(null); load(); setReceipt(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const openLedger = (r) => {
    if (!r.customer_id) { toast.error("Customer not linked"); return; }
    const custBills = rows.filter((x) => x.customer_id === r.customer_id).sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    setLedger({ customer_id: r.customer_id, name: r.customer_name, bills: custBills });
  };

  const waLink = (r) => {
    const mobile = ""; // billing rows don't have mobile — WhatsApp opens contact picker
    const msg = `Namaste, this is your bill from DigitalSetu ERP.\n\nInvoice: ${r.app_no}\nService: ${r.service_name}\nAmount: ₹${(r.total || 0).toLocaleString("en-IN")}\nStatus: ${r.payment_status}\n\nThank you.`;
    return `https://wa.me/${mobile}?text=${encodeURIComponent(msg)}`;
  };

  const emailLink = (r) => {
    const subject = `Invoice ${r.app_no} — ${r.service_name}`;
    const body = `Dear ${r.customer_name},\n\nPlease find below your invoice details from DigitalSetu ERP.\n\nInvoice: ${r.app_no}\nService: ${r.service_name}\nSubtotal: ₹${((r.total || 0) - (r.gst_amount || 0)).toLocaleString("en-IN")}\nGST: ₹${(r.gst_amount || 0).toLocaleString("en-IN")}\nTotal: ₹${(r.total || 0).toLocaleString("en-IN")}\nStatus: ${r.payment_status}${r.payment_method ? " · " + r.payment_method : ""}\n\nRegards,\nDigitalSetu ERP`;
    return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const exportCsv = () => {
    if (!filtered.length) { toast.error("Nothing to export"); return; }
    const header = ["Invoice #", "Date", "Customer", "Service", "Department", "Subtotal", "GST", "Total", "Status", "Method", "Txn Ref"];
    const rowsCsv = filtered.map((r) => [
      r.app_no, (r.created_at || "").slice(0, 16).replace("T", " "),
      r.customer_name, r.service_name, r.department || "",
      ((r.total || 0) - (r.gst_amount || 0)).toFixed(2),
      (r.gst_amount || 0).toFixed(2), (r.total || 0).toFixed(2),
      r.payment_status, r.payment_method || "", r.payment_txn_ref || "",
    ]);
    const csv = [header, ...rowsCsv].map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url; a.download = `billing-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-8 max-w-[1400px]">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-600 font-semibold mb-2">Ledger</div>
          <h1 className="text-4xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Billing & Ledger</h1>
          <p className="text-sm text-slate-600 mt-2">Every invoice raised from applications · search, filter, share and reconcile in one place.</p>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40 h-11" data-testid="billing-period"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All time</SelectItem>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={exportCsv} data-testid="billing-export-csv" className="bg-slate-900 hover:bg-slate-800 text-white h-11 gap-2"><DownloadSimple size={16} /> Export CSV</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-4">
        <Kpi testid="kpi-invoice-count" label="Total Bills" value={kpis.count} icon={FileText} tone="blue"
          onClick={() => setStatusFilter("All")} active={statusFilter === "All"} />
        <Kpi testid="kpi-collected" label="Collected" value={inr(kpis.collected)} icon={CurrencyInr} tone="green"
          onClick={() => setStatusFilter("Paid")} active={statusFilter === "Paid"} />
        <Kpi testid="kpi-cash" label="Cash" value={inr(kpis.cash)} icon={CurrencyInr} tone="orange"
          onClick={() => { setStatusFilter("Paid"); setMethodFilter("Cash"); }} active={methodFilter === "Cash"} />
        <Kpi testid="kpi-online" label="Online (UPI+Card+NB+Wallet)" value={inr(kpis.online)} icon={CreditCard} tone="blue"
          onClick={() => { setStatusFilter("Paid"); setMethodFilter("Online"); }} active={methodFilter === "Online"} />
        <Kpi testid="kpi-gst" label="GST · Collected" value={inr(kpis.gst)} icon={Receipt} tone="orange" />
        <Kpi testid="kpi-gst-count" label="GST Bills" value={kpis.gstCount} icon={FileText} tone="blue" />
        <Kpi testid="kpi-pos" label={`POS Bills · ${inr(kpis.posCollection)}`} value={kpis.posBills} icon={ShoppingCart} tone="green"
          onClick={() => setSource("POS")} active={source === "POS"} />
        <Kpi testid="kpi-outstanding" label="Outstanding" value={inr(kpis.outstanding)} icon={Warning} tone="amber"
          onClick={() => setStatusFilter("Unpaid")} active={statusFilter === "Unpaid"} />
      </div>

      {/* Source toggle */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <button onClick={() => setSource("Application")} data-testid="billing-source-application"
          className={`px-4 h-9 rounded-full text-sm font-medium transition-colors ${source === "Application" ? "bg-slate-900 text-white" : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-100"}`}>
          Application Bills · {rows.length}
        </button>
        <button onClick={() => setSource("POS")} data-testid="billing-source-pos"
          className={`px-4 h-9 rounded-full text-sm font-medium transition-colors ${source === "POS" ? "bg-orange-600 text-white" : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-100"}`}>
          POS Bills · {posRows.length}
        </button>
      </div>

      <Card className="p-4 bg-white border-slate-200 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2 relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="billing-search"
              placeholder="Invoice #, customer, service, portal #, UTR…"
              value={q} onChange={(e) => setQ(e.target.value)}
              className="pl-9 h-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="billing-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All statuses</SelectItem>
              <SelectItem value="Paid">Paid</SelectItem>
              <SelectItem value="Unpaid">Unpaid</SelectItem>
            </SelectContent>
          </Select>
          <Select value={methodFilter} onValueChange={setMethodFilter}>
            <SelectTrigger data-testid="billing-method-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="All">Any method</SelectItem>
              <SelectItem value="Online">Online (UPI+Card+NB+Wallet)</SelectItem>
              {PAY_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="bg-white border-slate-200 overflow-hidden" data-testid="billing-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Invoice #</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Date</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Customer</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Service</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500 text-right">GST</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500 text-right">Paid</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500 text-right">Balance</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500 text-right">Total</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={10} className="text-center text-slate-500 py-16">{rows.length === 0 ? "No invoices yet." : "No invoices match these filters."}</TableCell></TableRow>
            )}
            {filtered.map((r) => (
              <TableRow key={r.id} className="hover:bg-slate-50">
                <TableCell>
                  <button type="button" onClick={() => setReceipt(r)} data-testid={`bill-open-${r.id}`} className="font-mono text-xs text-orange-700 hover:text-orange-800 hover:underline underline-offset-2">
                    {r.app_no}
                  </button>
                  {r.payment_txn_ref && <div className="text-[10px] font-mono text-emerald-700">Ref: {r.payment_txn_ref}</div>}
                </TableCell>
                <TableCell className="text-xs font-mono text-slate-600">{(r.created_at || "").slice(0, 10)}</TableCell>
                <TableCell><CustomerLink id={r.customer_id} name={r.customer_name} /></TableCell>
                <TableCell><div className="text-sm">{r.service_name}</div>{r.department && <div className="text-[10px] text-slate-500">{r.department}</div>}</TableCell>
                <TableCell className="text-right text-sm text-slate-600">{inr(r.gst_amount || 0)}</TableCell>
                <TableCell className="text-right font-semibold">{inr(r.total)}</TableCell>
                <TableCell>
                  {r.payment_status === "Paid"
                    ? <Badge className={STATUS_TONE.Paid} style={{ borderColor: METHOD_COLORS[r.payment_method] }}>Paid · {r.payment_method}</Badge>
                    : <Badge variant="outline" className={STATUS_TONE.Unpaid}>Unpaid</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex items-center gap-1">
                    {r.payment_status !== "Paid" && (
                      <Button size="sm" onClick={() => quickCash(r)} data-testid={`bill-quickcash-${r.id}`} className="h-8 px-2 gap-1 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white uppercase tracking-wider">
                        <CurrencyInr size={12} weight="bold" /> Cash
                      </Button>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`bill-menu-${r.id}`}><DotsThreeVertical size={16} /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-white" align="end">
                        <DropdownMenuItem onClick={() => setReceipt(r)}><Receipt size={14} className="mr-2" /> View Receipt</DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <a href={waLink(r)} target="_blank" rel="noreferrer" className="flex items-center"><WhatsappLogo size={14} className="mr-2 text-emerald-600" weight="fill" /> WhatsApp Bill</a>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <a href={emailLink(r)} className="flex items-center"><Envelope size={14} className="mr-2" /> Email Bill</a>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openLedger(r)} data-testid={`bill-ledger-${r.id}`}><BookOpen size={14} className="mr-2" /> Customer Ledger</DropdownMenuItem>
                        {r.payment_status !== "Paid" && (
                          <DropdownMenuItem onClick={() => setPay({ id: r.id, method: "UPI", txn_ref: "", app: r })} data-testid={`bill-pay-${r.id}`}>
                            <CurrencyInr size={14} className="mr-2" /> Record Payment…
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {filtered.length > 0 && (
          <div className="border-t border-slate-200 bg-slate-50/70 px-4 py-3 flex flex-wrap items-center justify-between gap-4 text-xs" data-testid="billing-footer-summary">
            <div className="text-slate-500">Showing <b className="text-slate-900">{filtered.length}</b> of <b className="text-slate-900">{source === "POS" ? posRows.length : rows.length}</b> {source === "POS" ? "POS bills" : "invoices"}</div>
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-slate-500">Collected: <b className="text-emerald-700">{inr(kpis.collected)}</b></span>
              <span className="text-slate-500">GST: <b className="text-orange-700">{inr(kpis.gst)}</b></span>
              <span className="text-slate-500">Outstanding: <b className="text-amber-700">{inr(kpis.outstanding)}</b></span>
              <span className="text-slate-500">Cash / Online: <b className="text-slate-900">{inr(kpis.cash)} / {inr(kpis.online)}</b></span>
            </div>
          </div>
        )}
      </Card>

      {/* Receipt dialog */}
      <Dialog open={!!receipt} onOpenChange={(v) => !v && setReceipt(null)}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "var(--font-heading)" }}>Invoice · {receipt?.app_no}</DialogTitle>
            <DialogDescription className="sr-only">Printable GST invoice with service breakdown, GST amount and total.</DialogDescription>
          </DialogHeader>
          {receipt && (
            <div className="print-area py-2 text-sm" data-testid="bill-receipt-body">
              <div className="text-center border-b border-dashed border-slate-300 pb-3 mb-3">
                <div className="font-bold text-lg" style={{ fontFamily: "var(--font-heading)" }}>PRIYANKA ENTERPRISE · ERP</div>
                <div className="text-xs text-slate-500">GST Invoice · Aaple Sarkar / MahaOnline</div>
              </div>
              <RRow label="Invoice #" value={receipt.app_no} mono />
              <RRow label="Date" value={(receipt.created_at || "").slice(0, 16).replace("T", " ")} />
              <RRow label="Customer" value={receipt.customer_name} />
              <RRow label="Service" value={receipt.service_name} />
              {receipt.department && <RRow label="Department" value={receipt.department} />}
              {receipt.portal_app_no && <RRow label="Portal #" value={receipt.portal_app_no} mono />}
              <div className="border-t border-dashed border-slate-300 my-2" />
              {receipt.govt_fee > 0 && <RRow label="Govt Fee" value={inr(receipt.govt_fee)} />}
              {receipt.service_charge > 0 && <RRow label="Service Charge" value={inr(receipt.service_charge)} />}
              {receipt.printing_charge > 0 && <RRow label="Printing" value={inr(receipt.printing_charge)} />}
              {receipt.scanning_charge > 0 && <RRow label="Scanning" value={inr(receipt.scanning_charge)} />}
              {receipt.affidavit_charge > 0 && <RRow label="Affidavit" value={inr(receipt.affidavit_charge)} />}
              {receipt.lamination_charge > 0 && <RRow label="Lamination" value={inr(receipt.lamination_charge)} />}
              {receipt.discount > 0 && <RRow label="Discount" value={`- ${inr(receipt.discount)}`} />}
              <RRow label={`GST (${receipt.gst_percent}%)`} value={inr(receipt.gst_amount)} />
              <div className="border-t border-dashed border-slate-300 my-3" />
              <div className="flex justify-between items-center">
                <span className="text-sm font-semibold">TOTAL</span>
                <span className="text-2xl font-bold text-orange-600" style={{ fontFamily: "var(--font-heading)" }}>{inr(receipt.total)}</span>
              </div>
              <div className="mt-3 pt-3 border-t border-dashed border-slate-300 text-xs">
                <RRow label="Status" value={receipt.payment_status + (receipt.payment_method ? ` · ${receipt.payment_method}` : "")} />
                {receipt.payment_txn_ref && <RRow label="Txn Ref" value={receipt.payment_txn_ref} mono />}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceipt(null)}>Close</Button>
            <Button className="bg-slate-900 hover:bg-slate-800 gap-2" onClick={() => window.print()} data-testid="bill-print"><Printer size={14} /> Print</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record payment dialog */}
      <Dialog open={!!pay} onOpenChange={(v) => !v && setPay(null)}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "var(--font-heading)" }}>Record Payment</DialogTitle>
            <DialogDescription className="sr-only">Select payment method and optionally add a transaction reference.</DialogDescription>
          </DialogHeader>
          {pay && (
            <div className="py-2 space-y-4">
              <div className="text-sm text-slate-600">Invoice <span className="font-mono">{pay.app.app_no}</span> · <span className="font-semibold text-slate-900">{inr(pay.app.total)}</span></div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-600">Method</Label>
                <div className="grid grid-cols-5 gap-1.5">
                  {PAY_METHODS.map((m) => (
                    <button key={m} type="button"
                      data-testid={`bill-pay-method-${m.replace(/\s/g, "-").toLowerCase()}`}
                      onClick={() => setPay({ ...pay, method: m })}
                      className={`px-2 py-2 rounded-md border text-xs font-medium transition-colors ${pay.method === m ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}>
                      {m}
                    </button>
                  ))}
                </div>
              </div>
              {pay.method !== "Cash" && (
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-600">Txn Ref / UTR</Label>
                  <Input data-testid="bill-pay-txn-ref" value={pay.txn_ref || ""} onChange={(e) => setPay({ ...pay, txn_ref: e.target.value })} placeholder={pay.method === "UPI" ? "e.g. 4267...@ybl" : "Reference / RRN"} />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPay(null)}>Cancel</Button>
            <Button data-testid="bill-pay-confirm" onClick={doPay} className="bg-orange-600 hover:bg-orange-700 text-white">Confirm Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer ledger dialog */}
      <Dialog open={!!ledger} onOpenChange={(v) => !v && setLedger(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "var(--font-heading)" }}>Customer Ledger · {ledger?.name}</DialogTitle>
            <DialogDescription className="sr-only">Chronological ledger of all bills raised and payments received for this customer with a running balance.</DialogDescription>
          </DialogHeader>
          {ledger && (() => {
            let balance = 0;
            const entries = [];
            ledger.bills.forEach((b) => {
              balance += b.total || 0;
              entries.push({ date: b.created_at, kind: "Bill", ref: b.app_no, service: b.service_name, debit: b.total || 0, credit: 0, running: balance });
              if (b.payment_status === "Paid" && b.paid_at) {
                balance -= b.total || 0;
                entries.push({ date: b.paid_at, kind: `Payment · ${b.payment_method}`, ref: b.payment_txn_ref || b.app_no, service: b.service_name, debit: 0, credit: b.total || 0, running: balance });
              }
            });
            const totalBilled = ledger.bills.reduce((s, b) => s + (b.total || 0), 0);
            const totalPaid = ledger.bills.filter((b) => b.payment_status === "Paid").reduce((s, b) => s + (b.total || 0), 0);
            return (
              <div className="space-y-4 py-2" data-testid="ledger-body">
                <div className="grid grid-cols-3 gap-3">
                  <MiniStat label="Total Billed" value={inr(totalBilled)} tone="slate" />
                  <MiniStat label="Total Received" value={inr(totalPaid)} tone="green" />
                  <MiniStat label="Closing Balance" value={inr(balance)} tone={balance > 0 ? "amber" : "green"} />
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-[10px] uppercase tracking-widest">Date</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest">Entry</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest">Reference</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-right">Debit</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-right">Credit</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-right">Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">No entries.</TableCell></TableRow>}
                    {entries.map((e, i) => (
                      <TableRow key={i} className={e.kind.startsWith("Payment") ? "bg-emerald-50/30" : ""}>
                        <TableCell className="text-xs font-mono text-slate-600">{(e.date || "").slice(0, 10)}</TableCell>
                        <TableCell className="text-sm">{e.kind}</TableCell>
                        <TableCell className="text-xs font-mono">{e.ref}<div className="text-[10px] text-slate-500">{e.service}</div></TableCell>
                        <TableCell className="text-right text-sm">{e.debit ? inr(e.debit) : "—"}</TableCell>
                        <TableCell className="text-right text-sm text-emerald-700">{e.credit ? inr(e.credit) : "—"}</TableCell>
                        <TableCell className="text-right text-sm font-semibold">{inr(e.running)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setLedger(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RRow({ label, value, mono }) {
  return (
    <div className="flex justify-between text-xs py-0.5">
      <span className="text-slate-600">{label}</span>
      <span className={`font-medium text-slate-900 ${mono ? "font-mono" : ""}`}>{value || "—"}</span>
    </div>
  );
}

function MiniStat({ label, value, tone }) {
  const t = {
    slate: "text-slate-900",
    green: "text-emerald-700",
    amber: "text-amber-700",
  }[tone] || "text-slate-900";
  return (
    <div className="p-3 rounded-md border border-slate-200 bg-slate-50">
      <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">{label}</div>
      <div className={`text-xl font-bold mt-1 ${t}`} style={{ fontFamily: "var(--font-heading)" }}>{value}</div>
    </div>
  );
}
